
import { useState } from "react";
import { useAuth } from "@/components/AuthProvider";
import { CurrentPlan } from "./components/CurrentPlan";
import { PlanCard } from "./components/PlanCard";
import { useSubscription as useSubscriptionDetails } from "@/hooks/useSubscription";
import { useSubscription as useSubscriptionHandler } from "./hooks/useSubscription";
import { 
  PRO_FEATURES, 
  PREMIUM_FEATURES,
  TEAMS_FEATURES,
  PRO_TIER,
  PREMIUM_TIER,
  TEAMS_TIER,
  PRO_TIERS,
  PREMIUM_TIERS
} from "./types";
import { PlanTierOption } from "@/types/subscription";
import { useSubscriptionFeatures } from "@/hooks/useSubscriptionFeatures";
import { showPlansSection, getButtonConfig } from "./utils/planUtils";
import { useStudentLimit } from "@/hooks/useStudentLimit";
import { toast } from "sonner";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";
import { useTranslations } from "@/hooks/useTranslations";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export const SubscriptionSection = () => {
  const { profile } = useAuth();
  const { isLoading: isLoadingSubscription, handleSubscription } = useSubscriptionHandler();
  const { plans, currentPlan, currentTier } = useSubscriptionDetails();
  const { hasFeature } = useSubscriptionFeatures();
  const { currentCount } = useStudentLimit();
  const { language } = useLanguage();
  const { getPath } = useLanguagePath();
  const { t } = useTranslations("dashboard");
  
  const [selectedProTier, setSelectedProTier] = useState<PlanTierOption>(PRO_TIERS[0]);
  const [selectedPremiumTier, setSelectedPremiumTier] = useState<PlanTierOption>(PREMIUM_TIERS[0]);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);

  const getCurrentPlanType = (): 'free' | 'trial' | 'pro' | 'premium' => {
    if (!profile?.subscription_plan) return 'free';
    if (profile.subscription_plan === "trial") return "trial";
    if (profile.subscription_plan === "pro") return "pro";
    if (profile.subscription_plan === "premium") return "premium";
    return 'free';
  };

  const hasActivePaidPlan = (): boolean => {
    return (
      profile?.subscription_plan === 'pro' ||
      profile?.subscription_plan === 'premium'
    );
  };

  // Check if user had a paid plan that expired
  const hadPaidPlanThatExpired = () => {
    return (profile?.subscription_plan === 'pro' || profile?.subscription_plan === 'premium') && 
           (profile?.subscription_status === 'canceled' || profile?.subscription_status === 'past_due');
  };

  // Get the appropriate upgrade title
  const getUpgradeTitle = () => {
    if (!profile?.subscription_plan || profile.subscription_plan === 'free') {
      return t('subscription.upgradeTitle');
    }
    
    if (profile.subscription_plan === 'trial') {
      return t('subscription.trialUpgradeTitle');
    }
    
    if (hadPaidPlanThatExpired()) {
      return t('subscription.renewSubscription');
    }
    
    return t('subscription.managePlanTitle');
  };

  const isPro = getCurrentPlanType() === 'pro';
  const isPremium = getCurrentPlanType() === 'premium';
  const isTrial = getCurrentPlanType() === 'trial';

  const canManageOnStripe = hasActivePaidPlan();

  const openStripePortal = async () => {
    try {
      setIsLoadingPortal(true);

      // Get current path including language prefix
      const currentPath = window.location.pathname;

      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: {
          currentPath: currentPath  // Pass current path to edge function
        }
      });

      if (error || !data?.url) {
        toast.error(language === 'pt'
          ? "Erro inesperado ao abrir o portal do Stripe."
          : "Unexpected error opening Stripe portal.");
        if (error) {
          console.error("Stripe portal error", error);
        }
        if (data?.error) {
          console.error("Stripe portal error (edge data):", data.error);
        }
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(language === 'pt'
        ? "Erro inesperado ao abrir o portal do Stripe."
        : "Unexpected error opening Stripe portal.");
      console.error("Unexpected error opening Stripe portal:", e);
    } finally {
      setIsLoadingPortal(false);
    }
  };

  const handlePlanChange = (priceId: string, maxStudents: number) => {
    if (canManageOnStripe) {
      openStripePortal();
      return;
    }
    if (currentTier && maxStudents < currentTier.maxStudents && currentCount > maxStudents) {
      toast.error(language === 'pt'
        ? `Não é possível fazer downgrade para este plano. Você tem ${currentCount} alunos, mas o plano selecionado permite apenas ${maxStudents}.`
        : `Cannot downgrade to this plan. You have ${currentCount} students, but the selected plan only allows ${maxStudents}.`);
      return;
    }
    handleSubscription(priceId);
  };

  const isCurrentTierSelected = (tier: PlanTierOption): boolean => {
    if (!profile?.subscription_tier) return false;
    return profile.subscription_tier === tier.tier;
  };

  // Função para traduzir as features
  const translateFeatures = (features) => {
    return features.map(feature => ({
      ...feature,
      text: t(feature.text)
    }));
  };

  return (
    <div className="space-y-8">
      <CurrentPlan profile={profile} />

      <div>
        <h3 className="text-2xl font-semibold mb-6">
          {getUpgradeTitle()}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <PlanCard
            title={t('subscription.professionalPlan')}
            price={PRO_TIER.price}
            features={translateFeatures(PRO_FEATURES)}
            isCurrentPlan={isPro || isTrial}
            buttonConfig={{
              text: canManageOnStripe ? 'subscription.buttons.manage' : 'subscription.buttons.upgrade',
              variant: canManageOnStripe ? 'outline' : 'default'
            }}
            isLoading={isPro ? isLoadingPortal : isLoadingSubscription}
            onSubscribe={() => handlePlanChange(PRO_TIER.priceId, PRO_TIER.maxStudents)}
            hasTrial={isTrial || profile?.subscription_plan === 'free'}
            selectedTier={PRO_TIER}
            isCurrentTier={isCurrentTierSelected(PRO_TIER)}
          />

          <PlanCard
            title={t('subscription.premiumPlan')}
            price={PREMIUM_TIER.price}
            features={translateFeatures(PREMIUM_FEATURES)}
            isCurrentPlan={isPremium}
            buttonConfig={{
              text: canManageOnStripe ? 'subscription.buttons.manage' : 'subscription.buttons.upgrade',
              variant: canManageOnStripe ? 'outline' : 'default'
            }}
            isLoading={isPremium ? isLoadingPortal : isLoadingSubscription}
            onSubscribe={() => handlePlanChange(PREMIUM_TIER.priceId, PREMIUM_TIER.maxStudents)}
            selectedTier={PREMIUM_TIER}
            isCurrentTier={isCurrentTierSelected(PREMIUM_TIER)}
            isRecommended={true}
          />

          <PlanCard
            title={t('subscription.teamsPlan')}
            price={TEAMS_TIER.price}
            features={translateFeatures(TEAMS_FEATURES)}
            isCurrentPlan={false}
            buttonConfig={{
              text: 'subscription.buttons.upgrade',
              variant: 'outline'
            }}
            isLoading={false}
            onSubscribe={() => {}}
            selectedTier={TEAMS_TIER}
            isCurrentTier={false}
            isComingSoon={true}
          />
        </div>
      </div>
    </div>
  );
};
